import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { Confirm, Dropdown } from 'semantic-ui-react';
import { iRootState } from '../../store';
import { FieldLabel } from '../../styles';
import AuthorizationContainer from '../../components/AuthorizationContainer';
import UnitActionButton from './components/users/UnitActionButton';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';
import {
  getEstateUnits,
  updateEstateUnitStatus
} from '../../services/estate-unit';
import { EstateUnit, EstateUnitStatusEnum } from '../../types/EstateUnit';
import {
  dangerNotification,
  successNotification
} from '../../services/notification';
import EstateUnitsPanel from './components/EstateUnitsPanel';
import EstateUnitUsersPanel from './components/EstateUnitUsersPanel';
import DeactivateUnitConfirmModal from './components/DeactivateUnitConfirmModal';
import {
  EstateFilterRow,
  MasterDetailLayout,
  PageHeader,
  PageTitle,
  PageToolbar,
  PageWrapper
} from './styles';

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

type Props = ReturnType<typeof mapState> & RouteComponentProps;

const EstateUnitsListPage: React.FC<Props> = ({
  user,
  availableBrokersAndEstates,
  history
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialEstateId = queryParams.get('estateId') || '';

  const [units, setUnits] = useState<EstateUnit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEstateId, setSelectedEstateId] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [confirmUnit, setConfirmUnit] = useState<EstateUnit | null>(null);
  const [confirmAction, setConfirmAction] = useState<'ACTIVE' | 'INACTIVE' | null>(null);
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);

  const isBackOffice = isBackOfficeUser();
  const estateOptions = useMemo(
    () =>
      (availableBrokersAndEstates.estates || []).map(estate => ({
        key: estate.key,
        text: estate.text,
        value: String(estate.value?.id || estate.value)
      })),
    [availableBrokersAndEstates.estates]
  );

  const resolvedEstateId = useMemo(() => {
    if (selectedEstateId) {
      return selectedEstateId;
    }

    if (initialEstateId) {
      return initialEstateId;
    }

    if (user.estate?.id) {
      return String(user.estate.id);
    }

    if (availableBrokersAndEstates.activeEstateId) {
      return String(availableBrokersAndEstates.activeEstateId);
    }

    if (estateOptions.length === 1) {
      return estateOptions[0].value;
    }

    return '';
  }, [
    selectedEstateId,
    initialEstateId,
    user.estate?.id,
    availableBrokersAndEstates.activeEstateId,
    estateOptions
  ]);

  const searchId = useMemo(() => {
    if (user.broker?.id) {
      return String(user.broker.id);
    }

    if (user.estate?.id) {
      return String(user.estate.id);
    }

    return resolvedEstateId;
  }, [user.broker?.id, user.estate?.id, resolvedEstateId]);

  const selectedUnit = useMemo(
    () => units.find(unit => unit.id === selectedUnitId && unit.status === EstateUnitStatusEnum.active) || null,
    [units, selectedUnitId]
  );

  const loadUnits = useCallback(async () => {
    if (!resolvedEstateId) {
      setUnits([]);
      setSelectedUnitId(null);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getEstateUnits({
        estateId: resolvedEstateId
      });
      setUnits(response || []);
    } catch {
      dangerNotification('Oops...', 'Não foi possível carregar as unidades.');
      setUnits([]);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedEstateId]);

  useEffect(() => {
    if (!selectedEstateId && resolvedEstateId) {
      setSelectedEstateId(resolvedEstateId);
    }
  }, [resolvedEstateId, selectedEstateId]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    if (!selectedUnitId) {
      return;
    }

    const stillActive = units.some(
      unit => unit.id === selectedUnitId && unit.status === EstateUnitStatusEnum.active
    );

    if (!stillActive) {
      setSelectedUnitId(null);
    }
  }, [units, selectedUnitId]);

  const handleEstateChange = (estateId: string) => {
    setSelectedEstateId(estateId);
    setSelectedUnitId(null);
  };

  const handleSelectUnit = (unit: EstateUnit) => {
    setSelectedUnitId(unit.id);
  };

  const handleEditUnit = (unit: EstateUnit) => {
    history.push(`/admin/estate-unit-edit/${unit.id}`);
  };

  const handleToggleUnitStatus = (unit: EstateUnit, action: 'ACTIVE' | 'INACTIVE') => {
    setConfirmUnit(unit);
    setConfirmAction(action);
  };

  const handleStatusToggle = async (unlinkUsers?: boolean) => {
    if (!confirmUnit || !confirmAction) {
      return;
    }

    try {
      await updateEstateUnitStatus(
        confirmUnit.id,
        confirmAction === 'ACTIVE'
          ? EstateUnitStatusEnum.active
          : EstateUnitStatusEnum.inactive,
        confirmAction === 'INACTIVE' ? unlinkUsers : undefined
      );
      successNotification('Sucesso', 'Status da unidade atualizado.');

      if (confirmAction === 'INACTIVE' && selectedUnitId === confirmUnit.id) {
        setSelectedUnitId(null);
      }

      setConfirmUnit(null);
      setConfirmAction(null);
      setUsersRefreshKey(prev => prev + 1);
      loadUnits();
    } catch (error) {
      const message =
        (error as { message?: string })?.message ||
        'Não foi possível atualizar o status da unidade.';
      dangerNotification('Oops...', message);
    }
  };

  const canManage =
    user.role === UserRolesEnum.brokerAdmin ||
    user.role === UserRolesEnum.brokerAnalyst ||
    user.role === UserRolesEnum.estateAdmin;

  return (
    <PageWrapper>
      <PageToolbar>
        {isBackOffice && (
          <EstateFilterRow>
            <FieldLabel>Imobiliária</FieldLabel>
            <Dropdown
              fluid
              search
              clearable
              selection
              placeholder="Selecionar"
              options={estateOptions}
              value={selectedEstateId}
              disabled={estateOptions.length === 1}
              onChange={(_, { value }) => handleEstateChange(String(value || ''))}
            />
          </EstateFilterRow>
        )}

        {canManage && resolvedEstateId && (
          <AuthorizationContainer
            action={ActionEnum.create}
            feature={FeatureEnum.estateUnits}
          >
            <UnitActionButton
              variant="primary"
              onClick={() =>
                history.push(`/admin/estate-unit?estateId=${resolvedEstateId}`)
              }
            >
              + Nova unidade
            </UnitActionButton>
          </AuthorizationContainer>
        )}
      </PageToolbar>

      <MasterDetailLayout>
        <EstateUnitsPanel
          units={units}
          isLoading={isLoading}
          selectedUnitId={selectedUnitId}
          canManage={canManage}
          onSelectUnit={handleSelectUnit}
          onEditUnit={handleEditUnit}
          onToggleUnitStatus={handleToggleUnitStatus}
        />

        <EstateUnitUsersPanel
          selectedUnit={selectedUnit}
          estateId={resolvedEstateId}
          searchId={searchId}
          canManage={canManage}
          refreshKey={usersRefreshKey}
        />
      </MasterDetailLayout>

      <Confirm
        open={Boolean(confirmUnit && confirmAction === 'ACTIVE')}
        content={`Deseja ativar a unidade "${confirmUnit?.name}"?`}
        cancelButton="Cancelar"
        confirmButton="Confirmar"
        onCancel={() => {
          setConfirmUnit(null);
          setConfirmAction(null);
        }}
        onConfirm={() => handleStatusToggle()}
      />

      <DeactivateUnitConfirmModal
        open={Boolean(confirmUnit && confirmAction === 'INACTIVE')}
        unit={confirmUnit}
        onClose={() => {
          setConfirmUnit(null);
          setConfirmAction(null);
        }}
        onConfirm={unlinkUsers => handleStatusToggle(unlinkUsers)}
      />
    </PageWrapper>
  );
};

export default connect(mapState)(EstateUnitsListPage);
